import { Box, CircularProgress, Typography } from "@mui/material";

import {
  buildSlideView,
  formatSlideClock,
  formatSlideCountdown,
  podiumAssetUrl,
  slideBackgroundUrl,
  slideCountdownRatio,
} from "../../helper/leaderboardSlide";

const PODIUM_FILL = {
  1: "#F6C431",
  2: "#E6E4DE",
  3: "#F0D08A",
};

const PODIUM_BLOCK_HEIGHT = {
  1: "clamp(120px, 26vh, 220px)",
  2: "clamp(96px, 19vh, 164px)",
  3: "clamp(84px, 15vh, 132px)",
};

const ASSET_CROP = {
  stage: {
    1: { w: 404, h: 353, x: 18, y: 29, cw: 140, ch: 95 },
    2: { w: 180, h: 180, x: 22, y: 48, cw: 140, ch: 94 },
    3: { w: 180, h: 180, x: 20, y: 48, cw: 139, ch: 94 },
  },
  trophy: {
    1: { w: 185, h: 399, x: 27, y: 240, cw: 142, ch: 159 },
    2: { w: 185, h: 399, x: 30, y: 301, cw: 131, ch: 98 },
    3: { w: 185, h: 399, x: 36, y: 303, cw: 118, ch: 96 },
  },
};

function CroppedAsset({ kind, place, width }) {
  const crop = ASSET_CROP[kind][place] || ASSET_CROP[kind][1];
  const scale = width / crop.cw;
  return (
    <Box sx={{ width, height: crop.ch * scale, overflow: "hidden", position: "relative", flexShrink: 0 }}>
      <Box
        component="img"
        alt=""
        src={podiumAssetUrl(kind, place)}
        sx={{
          position: "absolute",
          width: crop.w * scale,
          height: crop.h * scale,
          left: -crop.x * scale,
          top: -crop.y * scale,
          maxWidth: "none",
        }}
      />
    </Box>
  );
}

function MetricText({ value, unit, fontSize }) {
  return (
    <Box sx={{ fontWeight: 800, fontSize, color: "#111827", lineHeight: 1.2 }}>
      {value}
      {unit ? (
        <Box component="span" sx={{ ml: 0.5, fontSize: "0.82em", fontWeight: 700 }}>
          {unit}
        </Box>
      ) : null}
    </Box>
  );
}

function PodiumColumn({ slot }) {
  const place = slot.place;
  const blockHeight = PODIUM_BLOCK_HEIGHT[place];
  const blockWidth = place === 1 ? "86%" : "78%";

  return (
    <Box
      sx={{
        flex: place === 1 ? 1.15 : 1,
        minWidth: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-end",
      }}
    >
      {slot.filled ? (
        <>
          {slot.showTrophy ? (
            <CroppedAsset kind="trophy" place={place} width={place === 1 ? 118 : 92} />
          ) : (
            <Box sx={{ height: 12 }} />
          )}
          {slot.champion ? (
            <Box
              sx={{
                mt: 0.75,
                px: 1.25,
                py: 0.15,
                borderRadius: 999,
                bgcolor: "#111827",
                color: "#fff",
                fontSize: 12,
                fontWeight: 700,
                lineHeight: "20px",
              }}
            >
              榜首
            </Box>
          ) : null}
          <Typography
            sx={{
              mt: 0.75,
              px: 0.5,
              maxWidth: "100%",
              fontWeight: 800,
              fontSize: place === 1 ? 22 : 18,
              lineHeight: 1.25,
              color: "#111827",
              textAlign: "center",
            }}
          >
            {slot.name || "-"}
          </Typography>
          {slot.meta ? (
            <Typography sx={{ mt: 0.25, fontSize: 13, color: "#6b7280", textAlign: "center" }}>
              {slot.meta}
            </Typography>
          ) : null}
          <Box sx={{ mt: 0.75, mb: 1.25 }}>
            <MetricText value={slot.metricValue} unit={slot.metricUnit} fontSize={place === 1 ? 20 : 16} />
          </Box>
          <Box
            sx={{
              width: blockWidth,
              maxWidth: place === 1 ? 210 : 176,
              height: blockHeight,
              borderRadius: "18px 18px 10px 10px",
              bgcolor: PODIUM_FILL[place],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 12px 24px rgba(80, 60, 0, 0.08)",
            }}
          >
            <CroppedAsset kind="stage" place={place} width={place === 1 ? 118 : 96} />
          </Box>
        </>
      ) : (
        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Typography sx={{ fontSize: 14, color: "#9ca3af" }}>虛位以待</Typography>
          {slot.showWaiting ? (
            <Typography sx={{ mt: 0.25, fontSize: 12, color: "#b0b6c0" }}>尚未有人</Typography>
          ) : null}
          <Box
            sx={{
              mt: 1,
              width: blockWidth,
              maxWidth: place === 1 ? 210 : 176,
              height: blockHeight,
              opacity: 0.5,
              borderRadius: "18px",
              border: "2px dashed #9ca3af",
              bgcolor: "rgba(255,255,255,0.55)",
              display: "grid",
              placeItems: "center",
              color: "#9ca3af",
              fontSize: place === 1 ? 64 : 48,
              fontWeight: 800,
              lineHeight: 1,
            }}
          >
            {place}
          </Box>
        </Box>
      )}
    </Box>
  );
}

function ListCard({ slot }) {
  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "grid",
        gridTemplateColumns: "42px minmax(0, 1fr) auto",
        columnGap: 1.5,
        alignItems: "center",
        px: 2,
        borderRadius: "14px",
        bgcolor: slot.filled ? "rgba(255,255,255,0.94)" : "rgba(255,255,255,0.42)",
        border: slot.filled ? "1px solid rgba(255,255,255,0.8)" : "1.5px dashed rgba(156, 163, 175, 0.95)",
        boxShadow: slot.filled ? "0 6px 16px rgba(80, 60, 0, 0.05)" : "none",
      }}
    >
      <Box sx={{ fontWeight: 800, fontSize: 16, color: slot.filled ? "#374151" : "#9ca3af" }}>
        {slot.rank}
      </Box>
      {slot.filled ? (
        <Box sx={{ minWidth: 0, display: "flex", alignItems: "baseline", gap: 1 }}>
          <Box sx={{ fontWeight: 800, fontSize: 18, color: "#111827", whiteSpace: "nowrap" }}>{slot.name || "-"}</Box>
          {slot.meta ? (
            <Box
              sx={{
                minWidth: 0,
                fontSize: 14,
                color: "#9ca3af",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {slot.meta}
            </Box>
          ) : null}
        </Box>
      ) : (
        <Box sx={{ fontSize: 18, color: "#9ca3af" }}>-</Box>
      )}
      <Box sx={{ justifySelf: "end" }}>
        {slot.filled ? <MetricText value={slot.metricValue} unit={slot.metricUnit} fontSize={18} /> : null}
      </Box>
    </Box>
  );
}

function EmptyPanel({ panel }) {
  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 280,
        borderRadius: "28px",
        bgcolor: "rgba(255, 251, 240, 0.9)",
        px: { xs: 3, md: 5 },
        py: { xs: 4, md: 6 },
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      {panel.badge ? (
        <Box
          sx={{
            alignSelf: "flex-start",
            mb: 2,
            px: 1.5,
            py: 0.4,
            borderRadius: 999,
            bgcolor: "#F5C518",
            color: "#111827",
            fontSize: 14,
            fontWeight: 800,
          }}
        >
          {panel.badge}
        </Box>
      ) : null}
      <Typography sx={{ fontSize: "clamp(26px, 2.5vw, 36px)", fontWeight: 800, lineHeight: 1.35, color: "#111827" }}>
        {panel.headlineBefore}
        {panel.highlight ? (
          <Box component="span" sx={{ color: "#C4920A", fontSize: "1.15em" }}>
            {panel.highlight}
          </Box>
        ) : null}
        {panel.headlineAfter}
      </Typography>
      <Typography sx={{ mt: 1.5, fontSize: 16, color: "#6b7280" }}>{panel.detail}</Typography>
    </Box>
  );
}

function SlideTimer({ remainingMs, intervalMs, paused, onClose }) {
  const color = paused ? "#9ca3af" : "#3B6FE0";
  const width = `${slideCountdownRatio(remainingMs, intervalMs) * 100}%`;

  return (
    <Box
      component="button"
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onClose?.();
      }}
      aria-label="關閉"
      title="關閉"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.75,
        color,
        cursor: "pointer",
        position: "relative",
        zIndex: 6,
        px: 1.5,
        py: 0.75,
        border: 0,
        bgcolor: "transparent",
        font: "inherit",
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 3,
          borderRadius: 999,
          overflow: "hidden",
          bgcolor: paused ? "rgba(156, 163, 175, 0.35)" : "rgba(59, 111, 224, 0.2)",
        }}
      >
        <Box sx={{ width, height: "100%", borderRadius: 999, bgcolor: color }} />
      </Box>
      {/* <Box sx={{ minWidth: 28, fontSize: 12, fontWeight: 700, fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>
        {formatSlideCountdown(remainingMs)}
      </Box> */}
    </Box>
  );
}

function SlideDots({ count, index }) {
  if (count <= 1) {
    return null;
  }
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: 0.75, maxWidth: 280 }}>
      {Array.from({ length: count }, (_, dot) => (
        <Box
          key={dot}
          sx={{
            width: dot === index ? 28 : 8,
            height: 8,
            borderRadius: 999,
            bgcolor: dot === index ? "#3B6FE0" : "rgba(255,255,255,0.95)",
            boxShadow: "0 0 0 1px rgba(17, 24, 39, 0.08)",
          }}
        />
      ))}
    </Box>
  );
}

export default function LeaderboardSlide({
  code,
  background,
  rows,
  skillName,
  skillUnit,
  title,
  orgId,
  loading,
  error,
  updatedAt,
  dotCount,
  dotIndex,
  remainingMs,
  intervalMs,
  paused,
  onPrev,
  onNext,
  onTogglePause,
  onClose,
}) {
  const view = buildSlideView({
    code,
    rows,
    skillName,
    title,
    orgId,
    unit: skillUnit,
  });
  const clock = formatSlideClock(updatedAt);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100vw",
        height: "100vh",
        overflow: "hidden",
        color: "#111827",
        backgroundColor: "#fff8e8",
        backgroundImage: `url("${slideBackgroundUrl(background)}")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        userSelect: "none",
        display: "flex",
        flexDirection: "column",
        px: { xs: 3, md: 5 },
        pt: { xs: 2.5, md: 3.5 },
        pb: { xs: 1.5, md: 2 },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 3 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: "clamp(26px, 3vw, 40px)", fontWeight: 800, lineHeight: 1.2 }}>
            {view.title || "排行榜"}
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: 16, color: "#6b7280" }}>{view.subtitle}</Typography>
        </Box>
        <SlideDots count={dotCount} index={dotIndex} />
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          mt: 2,
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.05fr) minmax(0, 0.95fr)",
          gap: { xs: 2, md: 4 },
          "@media (max-width: 700px)": {
            gridTemplateColumns: "1fr",
          },
        }}
      >
        {loading ? (
          <Box sx={{ gridColumn: "1 / -1", display: "grid", placeItems: "center" }}>
            <CircularProgress size={32} sx={{ color: "#111827" }} />
          </Box>
        ) : null}

        {!loading && error ? (
          <Box sx={{ gridColumn: "1 / -1", display: "grid", placeItems: "center" }}>
            <Typography sx={{ fontSize: 20, fontWeight: 700 }}>{error}</Typography>
          </Box>
        ) : null}

        {!loading && !error ? (
          <>
            <Box sx={{ minHeight: 0, display: "flex", alignItems: "flex-end", justifyContent: "center", gap: 1.5 }}>
              {view.podium.map((slot) => (
                <PodiumColumn key={slot.place} slot={slot} />
              ))}
            </Box>
            <Box sx={{ minHeight: 0, py: { md: 1 } }}>
              {view.emptyPanel ? <EmptyPanel panel={view.emptyPanel} /> : null}
              {view.list ? (
                <Box sx={{ height: "100%", display: "flex", flexDirection: "column", gap: 1.1 }}>
                  {view.list.map((slot) => (
                    <ListCard key={slot.place} slot={slot} />
                  ))}
                </Box>
              ) : null}
            </Box>
          </>
        ) : null}
      </Box>

      <Box
        sx={{
          position: "relative",
          zIndex: 6,
          mt: 1.5,
          pt: 1.25,
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
          alignItems: "center",
          columnGap: 2,
          borderTop: "1px solid rgba(17, 24, 39, 0.12)",
          color: "#6b7280",
          fontSize: 13,
        }}
      >
        <Box sx={{ minWidth: 0 }}>{loading || error ? "" : view.footer}</Box>
        <Box sx={{ justifySelf: "center" }}>
          <SlideTimer
            remainingMs={remainingMs}
            intervalMs={intervalMs}
            paused={paused}
            onClose={onClose}
          />
        </Box>
        <Box sx={{ justifySelf: "end" }}>{clock ? `更新於 ${clock}` : ""}</Box>
      </Box>

      <Box sx={{ position: "absolute", inset: 0, zIndex: 5, display: "flex" }}>
        <Box sx={{ width: "25%", height: "100%" }} onClick={onPrev} />
        <Box sx={{ width: "50%", height: "100%" }} onClick={onTogglePause} />
        <Box sx={{ width: "25%", height: "100%" }} onClick={onNext} />
      </Box>
    </Box>
  );
}
